import * as React from 'react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import { NotificationItem } from '../../reducers/notifications';

export type MuteDuration = '1hour' | 'tomorrow' | '1week' | 'forever';

export interface MuteRequest {
    entityType: string;
    entityId: number;
    sourceLabel: string | null;
    until: string | null; // ISO date or null for forever
    duration: MuteDuration;
}

interface Props {
    item: NotificationItem;
    onStarToggle: (id: number) => void;
    onSnooze: (id: number, untilIso: string | null) => void;
    // Optional so existing call sites (drawer, etc.) keep working without mute
    // wiring. If omitted, the Mute submenu is hidden.
    onMute?: (item: NotificationItem, req: MuteRequest) => void;
    // Provided when rendering inside the Muted tab — replaces the Mute submenu
    // with a single "Unmute source" action (one click, no submenu). When both
    // onMute and onUnmute are passed, onUnmute wins (the row is already muted).
    onUnmute?: (item: NotificationItem) => void;
}

/**
 * Compute a "snooze until" date for one of the preset options. We build these
 * in the user's local timezone so "tomorrow 8 AM" means their 8 AM, not UTC.
 */
function computeSnoozeUntil(preset: 'tomorrow' | 'monday' | 'nextWeek'): string {
    const m = moment();
    switch (preset) {
        case 'tomorrow':
            // Tomorrow at 08:00 local. If it's already past 8 AM today we still
            // anchor on tomorrow (not today) — the user wants the alert to go
            // away until the next workday morning.
            return m.add(1, 'day').hour(8).minute(0).second(0).millisecond(0).toISOString();
        case 'monday':
            // Next Monday at 08:00. If today IS Monday, snooze to NEXT Monday
            // (else they get pinged again 7 hours later, defeating the purpose).
            return m.isoWeekday(8).hour(8).minute(0).second(0).millisecond(0).toISOString();
        case 'nextWeek':
            return m.add(7, 'days').hour(8).minute(0).second(0).millisecond(0).toISOString();
    }
}

/**
 * For mutes we offer four durations. Returns ISO date or null (forever).
 */
function computeMuteUntil(duration: MuteDuration): string | null {
    const m = moment();
    switch (duration) {
        case '1hour':
            return m.add(1, 'hour').toISOString();
        case 'tomorrow':
            return m.add(1, 'day').hour(8).minute(0).second(0).millisecond(0).toISOString();
        case '1week':
            return m.add(7, 'days').toISOString();
        case 'forever':
            return null;
    }
}

const NotificationRowMenu: React.FC<Props> = ({ item, onStarToggle, onSnooze, onMute, onUnmute }) => {
    const intl = useIntl();
    const [open, setOpen] = useState(false);
    const [snoozeOpen, setSnoozeOpen] = useState(false);
    const [muteOpen, setMuteOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    // Close on outside click — listen in capture phase so we don't race with
    // the button's onClick which also stops propagation.
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
                setOpen(false);
                setSnoozeOpen(false);
                setMuteOpen(false);
            }
        };
        document.addEventListener('mousedown', handler, true);
        return () => document.removeEventListener('mousedown', handler, true);
    }, [open]);

    const stopAndDo = useCallback((fn: () => void) => (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        fn();
    }, []);

    const handleKebab = stopAndDo(() => {
        setOpen((v) => !v);
        setSnoozeOpen(false);
        setMuteOpen(false);
    });

    const handleStar = stopAndDo(() => {
        onStarToggle(item.id);
        setOpen(false);
    });

    const handleSnoozePreset = (preset: 'tomorrow' | 'monday' | 'nextWeek') => stopAndDo(() => {
        onSnooze(item.id, computeSnoozeUntil(preset));
        setOpen(false);
        setSnoozeOpen(false);
    });

    const handleClearSnooze = stopAndDo(() => {
        onSnooze(item.id, null);
        setOpen(false);
        setSnoozeOpen(false);
    });

    const toggleSnoozeSub = stopAndDo(() => {
        setSnoozeOpen((v) => !v);
        setMuteOpen(false);
    });

    const toggleMuteSub = stopAndDo(() => {
        setMuteOpen((v) => !v);
        setSnoozeOpen(false);
    });

    // The source comes from the eager-loaded event payload on the notification.
    // When it's missing or null we hide both Mute and Unmute — there's nothing
    // to act against.
    const entityType = item.event?.source_type ?? null;
    const entityId = item.event?.source_id ?? null;
    const hasSource = !!entityType && entityId !== null;
    // In the Muted tab the row is, by definition, already muted — show Unmute
    // instead of Mute. In all other tabs show Mute (if wired).
    const canUnmute = !!onUnmute && hasSource;
    const canMute = !canUnmute && !!onMute && hasSource;

    const handleUnmute = stopAndDo(() => {
        if (!canUnmute || !onUnmute) return;
        onUnmute(item);
        setOpen(false);
    });

    const handleMutePreset = (duration: MuteDuration) => stopAndDo(() => {
        if (!canMute || !onMute) return;
        onMute(item, {
            entityType: entityType as string,
            entityId: entityId as number,
            // Use the rendered title as the human label. It's not always a clean
            // name ("New project: Enis Kameri" vs "Enis Kameri") but it gives
            // the Settings list far better context than "Project #1277".
            sourceLabel: item.rendered_title || null,
            until: computeMuteUntil(duration),
            duration,
        });
        setOpen(false);
        setMuteOpen(false);
    });

    const isSnoozed = !!item.muted_until && moment(item.muted_until).isAfter(moment());

    return (
        <div className="row-menu" ref={wrapRef}>
            <button
                type="button"
                className={`row-menu__trigger ${open ? 'is-open' : ''}`}
                onClick={handleKebab}
                aria-label={intl.formatMessage({ id: 'notifications.row.menu', defaultMessage: 'Actions' })}
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <circle cx="12" cy="5" r="2" />
                    <circle cx="12" cy="12" r="2" />
                    <circle cx="12" cy="19" r="2" />
                </svg>
            </button>

            {open && (
                <div className="row-menu__popover" role="menu" onClick={(e) => e.stopPropagation()}>
                    <button type="button" role="menuitem" className="row-menu__item" onClick={handleStar}>
                        <span className="row-menu__icon" aria-hidden="true">{item.starred ? '★' : '☆'}</span>
                        <FormattedMessage
                            id={item.starred ? 'notifications.row.unstar' : 'notifications.row.star'}
                            defaultMessage={item.starred ? 'Unstar' : 'Star'}
                        />
                    </button>

                    <button
                        type="button"
                        role="menuitem"
                        className={`row-menu__item ${snoozeOpen ? 'is-active' : ''}`}
                        onClick={toggleSnoozeSub}
                        aria-haspopup="menu"
                        aria-expanded={snoozeOpen}
                    >
                        <span className="row-menu__icon" aria-hidden="true">⏰</span>
                        <FormattedMessage id="notifications.row.snooze" defaultMessage="Snooze" />
                        <span className="row-menu__caret" aria-hidden="true">▸</span>
                    </button>

                    {snoozeOpen && (
                        <div className="row-menu__submenu" role="menu">
                            <button type="button" role="menuitem" className="row-menu__item" onClick={handleSnoozePreset('tomorrow')}>
                                <FormattedMessage id="notifications.row.snoozeTomorrow" defaultMessage="Until tomorrow 8 AM" />
                            </button>
                            <button type="button" role="menuitem" className="row-menu__item" onClick={handleSnoozePreset('monday')}>
                                <FormattedMessage id="notifications.row.snoozeMonday" defaultMessage="Until Monday 8 AM" />
                            </button>
                            <button type="button" role="menuitem" className="row-menu__item" onClick={handleSnoozePreset('nextWeek')}>
                                <FormattedMessage id="notifications.row.snoozeNextWeek" defaultMessage="In a week" />
                            </button>
                            {isSnoozed && (
                                <button type="button" role="menuitem" className="row-menu__item row-menu__item--danger" onClick={handleClearSnooze}>
                                    <FormattedMessage id="notifications.row.snoozeClear" defaultMessage="Clear snooze" />
                                </button>
                            )}
                        </div>
                    )}

                    {canMute && (
                        <>
                            <button
                                type="button"
                                role="menuitem"
                                className={`row-menu__item ${muteOpen ? 'is-active' : ''}`}
                                onClick={toggleMuteSub}
                                aria-haspopup="menu"
                                aria-expanded={muteOpen}
                            >
                                <span className="row-menu__icon" aria-hidden="true">🔕</span>
                                <FormattedMessage id="notifications.row.mute" defaultMessage="Mute source" />
                                <span className="row-menu__caret" aria-hidden="true">▸</span>
                            </button>

                            {muteOpen && (
                                <div className="row-menu__submenu" role="menu">
                                    <button type="button" role="menuitem" className="row-menu__item" onClick={handleMutePreset('1hour')}>
                                        <FormattedMessage id="notifications.mute.duration.1hour" defaultMessage="For 1 hour" />
                                    </button>
                                    <button type="button" role="menuitem" className="row-menu__item" onClick={handleMutePreset('tomorrow')}>
                                        <FormattedMessage id="notifications.mute.duration.tomorrow" defaultMessage="Until tomorrow 8 AM" />
                                    </button>
                                    <button type="button" role="menuitem" className="row-menu__item" onClick={handleMutePreset('1week')}>
                                        <FormattedMessage id="notifications.mute.duration.1week" defaultMessage="For 1 week" />
                                    </button>
                                    <button type="button" role="menuitem" className="row-menu__item row-menu__item--danger" onClick={handleMutePreset('forever')}>
                                        <FormattedMessage id="notifications.mute.duration.forever" defaultMessage="Forever" />
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {canUnmute && (
                        <button
                            type="button"
                            role="menuitem"
                            className="row-menu__item"
                            onClick={handleUnmute}
                        >
                            <span className="row-menu__icon" aria-hidden="true">🔔</span>
                            <FormattedMessage id="notifications.row.unmuteSource" defaultMessage="Unmute source" />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationRowMenu;
